import { RefObject, useEffect, useState } from "react";

// scroll positions are fractional on high density displays
const toleranceInPx = 1;

export interface ScrollEdges {
  canScrollToStart: boolean;
  canScrollToEnd: boolean;
}

/**
 * Tells whether a horizontally scrollable element hides content at its start or its end.
 * Measured again on scrolling, on resizing and when the amount of children changes.
 */
export function useScrollEdges(
  ref: RefObject<HTMLElement | null>,
  amountOfChildren: number,
): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({
    canScrollToStart: false,
    canScrollToEnd: false,
  });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    function measure() {
      const { scrollLeft, scrollWidth, clientWidth } = element!;
      const canScrollToStart = scrollLeft > toleranceInPx;
      const canScrollToEnd =
        scrollLeft + clientWidth < scrollWidth - toleranceInPx;
      setEdges((current) =>
        current.canScrollToStart === canScrollToStart &&
        current.canScrollToEnd === canScrollToEnd
          ? current
          : { canScrollToStart, canScrollToEnd },
      );
    }

    measure();
    element.addEventListener("scroll", measure, { passive: true });
    // the children too, a changed title changes the scroll width but not the element
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    Array.from(element.children).forEach((child) =>
      resizeObserver.observe(child),
    );
    return () => {
      element.removeEventListener("scroll", measure);
      resizeObserver.disconnect();
    };
  }, [ref, amountOfChildren]);

  return edges;
}
